/* eslint-disable react/require-default-props */
import React, { useState } from 'react';
import { useDualEventTracking } from '../../../utils/useDualEventTracking';
import { Spinner } from '@carvana/showroom-spinner';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  DisableAutoPayTileWrapper,
  ConfirmButton,
  CheckboxWrapper,
  LabelWrapper,
  DisclaimerWrapper,
  Title,
  ConsentButton,
  DisableAutopayCheckbox,
} from './TurnOffAutoPayTile.styles';
import BridgecrestInformation from '../../bank-information/bridgecrest-information';
import { fetchLoanDetails } from '../../../ducks/actions';
import { turnOffAutopay } from '../../../services/MyCarsService';
import ElectronicPaymentTerms from '../../future-packages/electronic-payment-terms-and-conditions';
import BridgecrestCustomError from '../../future-packages/bridgecrest-custom-error';
import OopsError from '../../future-packages/oops-error';

const DisableAutoPayTile = (props: {
  vehicleId: any;
  last4AccountNumber: any;
  confirmationLabel: string;
  disclaimerText: string;
  consentUrlText?: string;
}) => {
  const {
    vehicleId,
    last4AccountNumber,
    confirmationLabel,
    disclaimerText,
    consentUrlText,
  } = props;

  const { trackEvent } = useDualEventTracking();
  const history = useHistory();
  const dispatch = useDispatch();

  const [confirmed, setConfirmed] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  const [loading, setLoading] = useState(false);

  const [isBridgecrestError, setBridgecrestError] = useState(false);
  const [bridgecrestErrorMessage, setBridgecrestErrorMessage] = useState('');

  const [showOopsError, setOopsError] = useState(false);

  const title = 'Turn off Autopay for Your Bridgecrest Account';
  const buttonText = 'Turn off Autopay';

  const onDisableAutoPay = () => {
    trackEvent('turn_off_auto_pay_button_tap');
    setLoading(true);
    return turnOffAutopay(process.env.NODE_ENV, vehicleId, last4AccountNumber)
      .then((result) => {
        setLoading(false);
        handleResponse(result);
      })
      .catch((error) => {
        setLoading(false);
        setOopsError(true);
        console.error(error);
      });
  };

  const handleResponse = (result: any) => {
    /** redirecting to loan detail page after
     * successful api call to turning off auto-pay */
    if (result?.data?.isSuccess) {
      dispatch(fetchLoanDetails(process.env.NODE_ENV, vehicleId));
      trackEvent('cancel_auto_pay');
      history.push(`/loan-detail/${vehicleId}`);
      return;
    }
    if (result?.response) {
      const res = result.response;
      if (
        res.data.httpStatusCode !== 'OK' &&
        res.data.errorCode === 'BridgecrestGraphQLErrorException'
      ) {
        setBridgecrestErrorMessage(res.data.message);
        setBridgecrestError(true);
        return;
      }
      console.error(res.data.message);
    }
    setOopsError(true);
  };

  const onConfirmationClicked = () => {
    trackEvent('turn_off_auto_pay_check_box_tap');
    setConfirmed(!confirmed);
  };

  const toggleTermsModal = () => {
    if (!showTermsModal) {
      trackEvent('TermsAndConditions Screen');
      trackEvent('turn_off_auto_pay_terms_button_tap');
    } else {
      trackEvent('auto_pay_tos_dialogue_exit_tap');
    }
    setShowTermsModal(!showTermsModal);
  };

  const closeBridgecrestCustomError = () => {
    setBridgecrestError(false);
    setBridgecrestErrorMessage('');
    setConfirmed(!confirmed);
  };

  const closeOopsErrorModal = () => {
    setOopsError(false);
    setConfirmed(!confirmed);
  };

  return (
    <>
      {loading && (
        <Spinner
          isFullScreen
          className="spinnerWrapper fullScreenSpinner"
          opaqueBackground
        />
      )}
      <>
        <Title>{title}</Title>
        <DisableAutoPayTileWrapper>
          <CheckboxWrapper>
            <DisableAutopayCheckbox
              id="consentCheckbox"
              checked={confirmed}
              onClick={onConfirmationClicked}
            />
            <LabelWrapper>
              {confirmationLabel}
              <ConsentButton onClick={toggleTermsModal}>
                {consentUrlText}
              </ConsentButton>
            </LabelWrapper>
          </CheckboxWrapper>
          <DisclaimerWrapper>{disclaimerText}</DisclaimerWrapper>
          <ConfirmButton
            id="confirmDisableAutoPay"
            disabled={!confirmed}
            onClick={onDisableAutoPay}
          >
            {buttonText}
          </ConfirmButton>
          <BridgecrestInformation />
        </DisableAutoPayTileWrapper>

        {/* Electronics Terms and Conditions Modal */}
        <ElectronicPaymentTerms
          openModal={showTermsModal}
          toggleTermsModal={toggleTermsModal}
        />
        {/* Turnoff Autopay Failure Dialog */}
        <BridgecrestCustomError
          isOpen={isBridgecrestError}
          header="TURN OFF AUTOPAY"
          title="UNABLE TO TURN OFF AUTOPAY"
          message={bridgecrestErrorMessage}
          closeModal={closeBridgecrestCustomError}
        />
        {/* Oops Error Dialog */}
        <OopsError isOpen={showOopsError} closeModal={closeOopsErrorModal} />
      </>
    </>
  );
};

export default DisableAutoPayTile;
